import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import {
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  TrendingUp,
  TrendingDown,
  Calendar,
  Filter,
  Search,
  Download,
  Eye,
  BarChart3,
  PieChart,
  Activity,
  CreditCard,
  Banknote,
  Wallet,
  AlertCircle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  FileText,
  Printer,
  Share2,
} from 'lucide-react';

const Payouts = () => {
  const { vendor } = useAuth();
  const { socket } = useSocket();
  const navigate = useNavigate();
  
  // State management
  const [selectedPeriod, setSelectedPeriod] = useState('30');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [showPayoutDetails, setShowPayoutDetails] = useState(false);
  const [activeTab, setActiveTab] = useState('overview'); // overview, payouts, reports

  // Real-time payout notifications
  useEffect(() => {
    if (socket) {
      socket.on('payout_status_update', (data) => {
        if (data.vendor_id === vendor?.id) {
          // Refetch payout data when status changes
          refetchPayouts();
          refetchSummary();
        }
      });

      return () => {
        socket.off('payout_status_update');
      };
    }
  }, [socket, vendor]);

  // Queries
  const { data: payoutData, isLoading: payoutsLoading, refetch: refetchPayouts } = useQuery(
    ['vendor-payouts', currentPage, statusFilter, dateRange, searchTerm],
    async () => {
      const params = {
        page: currentPage,
        limit: 10,
        ...(statusFilter && { status: statusFilter }),
        ...(dateRange.from && { dateFrom: dateRange.from }),
        ...(dateRange.to && { dateTo: dateRange.to }),
        ...(searchTerm && { search: searchTerm })
      };
      const response = await api.get('/vendor/payouts', { params });
      return response.data;
    },
    {
      refetchInterval: 30000, // Refresh every 30 seconds
      retry: 3,
      retryDelay: 1000
    }
  );

  const { data: summaryData, isLoading: summaryLoading, refetch: refetchSummary } = useQuery(
    ['vendor-payout-summary', selectedPeriod],
    async () => {
      const response = await api.get(`/vendor/payouts/summary?period=${selectedPeriod}`);
      return response.data;
    },
    {
      refetchInterval: 60000, // Refresh every minute
      retry: 3,
      retryDelay: 1000
    }
  );

  const { data: financialData, isLoading: financialLoading } = useQuery(
    ['vendor-financial-reports', selectedPeriod],
    async () => {
      const response = await api.get(`/vendor/financial-reports?period=${selectedPeriod}`);
      return response.data;
    },
    {
      refetchInterval: 300000, // Refresh every 5 minutes
      retry: 3,
      retryDelay: 1000
    }
  );

  // Get payout details
  const getPayoutDetails = async (payoutId) => {
    try {
      const response = await api.get(`/vendor/payouts/${payoutId}`);
      setSelectedPayout(response.data);
      setShowPayoutDetails(true);
    } catch (error) {
      console.error('Error fetching payout details:', error);
    }
  };

  // Export functions
  const exportPayouts = () => {
    const data = payoutData?.payouts || [];
    const csvContent = [
      ['ID', 'Order ID', 'Amount', 'Status', 'Method', 'Reference', 'Created At', 'Processed At'],
      ...data.map(payout => [
        payout.id,
        payout.order?.id || '',
        payout.amount,
        payout.status,
        payout.payout_method || '',
        payout.payout_reference || '',
        new Date(payout.created_at).toLocaleDateString(),
        payout.processed_at ? new Date(payout.processed_at).toLocaleDateString() : ''
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payouts-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const printPayouts = () => {
    window.print();
  };

  // Helper functions
  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'processed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'processed':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Loading states
  if (payoutsLoading || summaryLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary-50 p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-secondary-900">Payout Dashboard</h1>
            <p className="text-secondary-600 mt-2">Manage and track your payouts and financial reports</p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => window.location.reload()}
              className="flex items-center px-4 py-2 bg-white border border-secondary-300 rounded-lg text-secondary-700 hover:bg-secondary-50"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </button>
            <button
              onClick={exportPayouts}
              className="flex items-center px-4 py-2 bg-white border border-secondary-300 rounded-lg text-secondary-700 hover:bg-secondary-50"
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </button>
            <button
              onClick={printPayouts}
              className="flex items-center px-4 py-2 bg-white border border-secondary-300 rounded-lg text-secondary-700 hover:bg-secondary-50"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print
            </button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'overview'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('payouts')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'payouts'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
            }`}
          >
            Payouts
          </button>
          <button
            onClick={() => setActiveTab('reports')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'reports'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
            }`}
          >
            Financial Reports
          </button>
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className=" ">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <DollarSign className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-secondary-600">Total Payouts</p>
                  <p className="text-2xl font-bold text-secondary-900">
                    {formatCurrency(summaryData?.summary?.totalAmount || 0)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Clock className="h-6 w-6 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-secondary-600">Pending</p>
                  <p className="text-2xl font-bold text-secondary-900">
                    {formatCurrency(summaryData?.summary?.pendingAmount || 0)}
                  </p>
                  <p className="text-sm text-secondary-500">
                    {summaryData?.summary?.pendingPayouts || 0} payouts
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <CheckCircle className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-secondary-600">Processed</p>
                  <p className="text-2xl font-bold text-secondary-900">
                    {formatCurrency(summaryData?.summary?.processedAmount || 0)}
                  </p>
                  <p className="text-sm text-secondary-500">
                    {summaryData?.summary?.processedPayouts || 0} payouts
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <BarChart3 className="h-6 w-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-secondary-600">Total Revenue</p>
                  <p className="text-2xl font-bold text-secondary-900">
                    {formatCurrency(financialData?.summary?.totalRevenue || 0)}
                  </p>
                  <p className="text-sm text-secondary-500">
                    {financialData?.summary?.orderCount || 0} orders
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Payout Trend Chart */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">Payout Trend</h3>
              <div className="h-64 flex items-center justify-center text-secondary-500">
                <div className="text-center">
                  <BarChart3 className="h-12 w-12 mx-auto mb-2" />
                  <p>Chart visualization would go here</p>
                  <p className="text-sm">Daily payout amounts over time</p>
                </div>
              </div>
            </div>

            {/* Payment Method Distribution */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">Payment Methods</h3>
              <div className="h-64 flex items-center justify-center text-secondary-500">
                <div className="text-center">
                  <PieChart className="h-12 w-12 mx-auto mb-2" />
                  <p>Chart visualization would go here</p>
                  <p className="text-sm">Revenue by payment method</p>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Payouts */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-secondary-200">
              <h3 className="text-lg font-semibold text-secondary-900">Recent Payouts</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-secondary-200">
                <thead className="bg-secondary-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Payout ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Order ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-secondary-200">
                  {payoutData?.payouts?.slice(0, 5).map((payout) => (
                    <tr key={payout.id} className="hover:bg-secondary-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-secondary-900">
                        #{payout.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500">
                        #{payout.order?.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-secondary-900">
                        {formatCurrency(payout.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(payout.status)}`}>
                          {getStatusIcon(payout.status)}
                          <span className="ml-1">{payout.status}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500">
                        {formatDate(payout.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => getPayoutDetails(payout.id)}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Payouts Tab */}
      {activeTab === 'payouts' && (
        <div className=" ">
          {/* Filters */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="processed">Processed</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  From Date
                </label>
                <input
                  type="date"
                  value={dateRange.from}
                  onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
                  className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  To Date
                </label>
                <input
                  type="date"
                  value={dateRange.to}
                  onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
                  className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Search
                </label>
                <input
                  type="text"
                  placeholder="Search payouts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Payouts Table */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-secondary-200">
              <h3 className="text-lg font-semibold text-secondary-900">All Payouts</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-secondary-200">
                <thead className="bg-secondary-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Payout ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Order ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Method
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-secondary-200">
                  {payoutData?.payouts?.map((payout) => (
                    <tr key={payout.id} className="hover:bg-secondary-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-secondary-900">
                        #{payout.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500">
                        #{payout.order?.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500">
                        {payout.order?.user?.name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-secondary-900">
                        {formatCurrency(payout.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(payout.status)}`}>
                          {getStatusIcon(payout.status)}
                          <span className="ml-1">{payout.status}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500">
                        {payout.payout_method || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500">
                        {formatDate(payout.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => getPayoutDetails(payout.id)}
                          className="text-primary-600 hover:text-primary-900 mr-2"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {payoutData?.pagination && (
              <div className="px-6 py-4 border-t border-secondary-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-secondary-700">
                    Showing {((payoutData.pagination.page - 1) * payoutData.pagination.limit) + 1} to{' '}
                    {Math.min(payoutData.pagination.page * payoutData.pagination.limit, payoutData.pagination.total)} of{' '}
                    {payoutData.pagination.total} results
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 border border-secondary-300 rounded-md text-sm disabled:opacity-50"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <span className="px-3 py-1 text-sm text-secondary-700">
                      Page {currentPage} of {payoutData.pagination.pages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(Math.min(payoutData.pagination.pages, currentPage + 1))}
                      disabled={currentPage === payoutData.pagination.pages}
                      className="px-3 py-1 border border-secondary-300 rounded-md text-sm disabled:opacity-50"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Financial Reports Tab */}
      {activeTab === 'reports' && (
        <div className=" ">
          {/* Financial Summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <DollarSign className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-secondary-600">Total Revenue</p>
                  <p className="text-2xl font-bold text-secondary-900">
                    {formatCurrency(financialData?.summary?.totalRevenue || 0)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-2 bg-red-100 rounded-lg">
                  <CreditCard className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-secondary-600">Admin Fees</p>
                  <p className="text-2xl font-bold text-secondary-900">
                    {formatCurrency(financialData?.summary?.totalAdminFees || 0)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Wallet className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-secondary-600">Net Revenue</p>
                  <p className="text-2xl font-bold text-secondary-900">
                    {formatCurrency(financialData?.summary?.netRevenue || 0)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Activity className="h-6 w-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-secondary-600">Avg Order Value</p>
                  <p className="text-2xl font-bold text-secondary-900">
                    {formatCurrency(financialData?.summary?.averageOrderValue || 0)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Method Breakdown */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">Payment Method Breakdown</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {financialData?.paymentMethodBreakdown && Object.entries(financialData.paymentMethodBreakdown).map(([method, data]) => (
                <div key={method} className="border border-secondary-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-secondary-900 capitalize">{method.replace('_', ' ')}</p>
                      <p className="text-2xl font-bold text-secondary-900">{formatCurrency(data.amount)}</p>
                      <p className="text-sm text-secondary-500">{data.count} orders</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-secondary-500">
                        {((data.amount / (financialData.summary.totalRevenue || 1)) * 100).toFixed(1)}%
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Revenue Chart */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">Revenue Trend</h3>
            <div className="h-64 flex items-center justify-center text-secondary-500">
              <div className="text-center">
                <TrendingUp className="h-12 w-12 mx-auto mb-2" />
                <p>Chart visualization would go here</p>
                <p className="text-sm">Daily revenue over time</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payout Details Modal */}
      {showPayoutDetails && selectedPayout && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-secondary-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-secondary-900">Payout Details</h3>
                <button
                  onClick={() => setShowPayoutDetails(false)}
                  className="text-secondary-400 hover:text-secondary-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="px-6 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-secondary-900 mb-4">Payout Information</h4>
                  <dl className="space-y-3">
                    <div>
                      <dt className="text-sm font-medium text-secondary-500">Payout ID</dt>
                      <dd className="text-sm text-secondary-900">#{selectedPayout.id}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-secondary-500">Amount</dt>
                      <dd className="text-sm font-medium text-secondary-900">{formatCurrency(selectedPayout.amount)}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-secondary-500">Status</dt>
                      <dd className="text-sm text-secondary-900">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedPayout.status)}`}>
                          {getStatusIcon(selectedPayout.status)}
                          <span className="ml-1">{selectedPayout.status}</span>
                        </span>
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-secondary-500">Payout Method</dt>
                      <dd className="text-sm text-secondary-900">{selectedPayout.payoutMethod || 'N/A'}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-secondary-500">Reference</dt>
                      <dd className="text-sm text-secondary-900">{selectedPayout.payoutReference || 'N/A'}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-secondary-500">Created At</dt>
                      <dd className="text-sm text-secondary-900">{formatDate(selectedPayout.createdAt)}</dd>
                    </div>
                    {selectedPayout.processedAt && (
                      <div>
                        <dt className="text-sm font-medium text-secondary-500">Processed At</dt>
                        <dd className="text-sm text-secondary-900">{formatDate(selectedPayout.processedAt)}</dd>
                      </div>
                    )}
                  </dl>
                </div>
                <div>
                  <h4 className="font-medium text-secondary-900 mb-4">Order Information</h4>
                  <dl className="space-y-3">
                    <div>
                      <dt className="text-sm font-medium text-secondary-500">Order ID</dt>
                      <dd className="text-sm text-secondary-900">#{selectedPayout.order?.id}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-secondary-500">Order Total</dt>
                      <dd className="text-sm text-secondary-900">{formatCurrency(selectedPayout.order?.total_amount)}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-secondary-500">Payment Method</dt>
                      <dd className="text-sm text-secondary-900 capitalize">{selectedPayout.order?.payment_method?.replace('_', ' ')}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-secondary-500">Customer</dt>
                      <dd className="text-sm text-secondary-900">{selectedPayout.order?.user?.name}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-secondary-500">Order Date</dt>
                      <dd className="text-sm text-secondary-900">{formatDate(selectedPayout.order?.created_at)}</dd>
                    </div>
                  </dl>
                  {selectedPayout.notes && (
                    <div className="mt-4">
                      <h4 className="font-medium text-secondary-900 mb-2">Notes</h4>
                      <p className="text-sm text-secondary-700 bg-secondary-50 p-3 rounded-md">
                        {selectedPayout.notes}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-secondary-200">
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowPayoutDetails(false)}
                  className="px-4 py-2 border border-secondary-300 rounded-md text-secondary-700 hover:bg-secondary-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payouts; 